import { getClerkInstance } from "@clerk/clerk-expo";

const SESSION_EXISTS_ERROR_CODE = "session_exists";

const isClerkSessionExistsError = (error: unknown): boolean => {
	if (typeof error !== "object" || error === null || !("errors" in error)) {
		return false;
	}

	const { errors } = error;
	if (!Array.isArray(errors)) {
		return false;
	}

	const firstError: unknown = errors[0];
	return (
		typeof firstError === "object" &&
		firstError !== null &&
		"code" in firstError &&
		firstError.code === SESSION_EXISTS_ERROR_CODE
	);
};

export const signInWithClerkTicket = async (
	ticket: string,
): Promise<boolean> => {
	const clerk = getClerkInstance();

	const attemptTicketSignIn = async (): Promise<boolean> => {
		const signInAttempt = await clerk.client?.signIn.create({
			strategy: "ticket",
			ticket,
		});

		if (signInAttempt?.status === "complete") {
			await clerk.setActive({
				session: signInAttempt.createdSessionId,
			});
			cachedToken = null;
			return true;
		}
		return false;
	};

	try {
		// Attempt the ticket sign-in first so an expired/malformed/consumed ticket
		// never signs out an already-authenticated user.
		return await attemptTicketSignIn();
	} catch (error) {
		if (!isClerkSessionExistsError(error)) {
			throw error;
		}

		// Clerk consumes the ticket before throwing session_exists, so remove+retry
		// always fails and logs the user out. The active session is valid — proceed.
		return true;
	}
};

// skipCache forces a Clerk network round-trip (~400ms) per call, and every API
// request awaits this in an axios interceptor. Memoize briefly and share the
// in-flight request; the short TTL bounds staleness of jwt-with-user metadata
// claims right after signup.
const TOKEN_CACHE_MS = 10_000;
let cachedToken: { value: string; expiresAt: number } | null = null;
let pendingToken: Promise<string | null> | null = null;

const fetchAuthToken = async (): Promise<string | null> => {
	let clerkInstance: ReturnType<typeof getClerkInstance>;
	try {
		clerkInstance = getClerkInstance();
	} catch {
		// On native, getClerkInstance() throws until ClerkProvider mounts with a
		// publishableKey. Pre-auth bootstrap requests (e.g. app config) run before
		// that, so treat an uninitialized Clerk as "no token" — same as web.
		return null;
	}
	const token = await clerkInstance?.session?.getToken({
		template: "jwt-with-user",
		skipCache: true,
	});
	return token ?? null;
};

export const getAuthToken = async (): Promise<string | null> => {
	if (cachedToken && Date.now() < cachedToken.expiresAt) {
		return cachedToken.value;
	}
	if (pendingToken) {
		return pendingToken;
	}
	pendingToken = fetchAuthToken();
	try {
		const token = await pendingToken;
		if (token) {
			cachedToken = { value: token, expiresAt: Date.now() + TOKEN_CACHE_MS };
		} else {
			cachedToken = null;
		}
		return token;
	} finally {
		pendingToken = null;
	}
};

export const getLogicalUserId = (): string | undefined => {
	const user = getClerkInstance()?.user;
	return user?.publicMetadata?.logicalId as string | undefined;
};

export const signOutAuth = async (): Promise<void> => {
	cachedToken = null;
	await getClerkInstance().signOut();
};
